import React, { useEffect, useMemo, useState, forwardRef,
  useImperativeHandle } from "react";
import { useTranslation } from "../../../../../i18n/useTranslation";
import {
  View,
  Text,
  StyleSheet,
  Platform,
} from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import  FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";
import { capturePosthogEvent } from "../../../../../utils/analytics";
import {
  validateDobDateFromDisplayString,
  formatDobInput,
  dateFromDobDisplayString,
  isDobDisplayComplete,
  DOB_MIN_AGE_SEND_OR_BILLPAY,
  DOB_MAX_AGE_YEARS,
} from "../../../../../helper/dobValidation";

const SENDER_DOB_AGE_OPTIONS = {
  minimumAgeYears: DOB_MIN_AGE_SEND_OR_BILLPAY,
  maximumAgeYears: DOB_MAX_AGE_YEARS,
} as const;

type Props = {
  onValidationChange?: (isValid: boolean) => void;
  testID?: string | undefined;
};

export interface SenderDateOfBirthStepHanle {
  submit: () => Promise<boolean>;
}

const SenderDateOfBirthStep = forwardRef<SenderDateOfBirthStepHanle, Props>(({ onValidationChange, testID }: Props, ref) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({
      dob: "",
    });
  
  const storedDobString = useSessionStore((s: SessionState) => (s as any).senderDOBString) || "";
  const [dob, setDob] = useState(
    () => (useSessionStore.getState() as any).senderDOBString || ""
  );
  const [showPicker, setShowPicker] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const setSenderDOBString = useSessionStore((s: SessionState) => (s as any).setSenderDOBString);
  const setSenderDOB = useSessionStore((s: SessionState) => (s as any).setSenderDOB);

  const isValid = useMemo(() => {
    if (!isDobDisplayComplete(dob)) return false;
    return !validateDobDateFromDisplayString(t, dob.trim(), true, SENDER_DOB_AGE_OPTIONS);
  }, [dob, t]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);
    
  // Keep local DOB in sync when store is hydrated (e.g., after AuthConsumer)
  useEffect(() => {
    setDob(storedDobString || "");
  }, [storedDobString]);

  // expose validate function
  useImperativeHandle(ref, () => ({
    submit: async () => {
      if (!dob.trim()) {
        const next = { dob: t("SENDER_DOB_ERROR_REQUIRED") };
        setErrors(next);
        return false;
      }
      if (isDobDisplayComplete(dob)) {
        const err = validateDobDateFromDisplayString(t, dob.trim(), true, SENDER_DOB_AGE_OPTIONS);
        const next = { dob: err };
        setErrors(next);
        return !Boolean(err);
      }
      const next = { dob: t("SENDER_DOB_ERROR_INCOMPLETE") };
      setErrors(next);
      return false;
    },
  }));

  // Mock ID verification function (replace with real API call)
  const verifyDOBWithID = async (enteredDob: string) => {
    console.log("Verifying DOB:", enteredDob);
    // Simulate backend check with previously uploaded ID
    return new Promise<boolean>((resolve) =>
      setTimeout(() => resolve(true), 1000) // always returns true for now
    );
  };

  const handleManualInput = async (rawText: string) => {
    const formatted = formatDobInput(rawText);
    setDob(formatted);
    try { setSenderDOBString?.(formatted); } catch {}
    // Apply kiosk-style validation on complete input (M/D/YYYY normalized to MM/DD/YYYY in formatDobInput).
    if (isDobDisplayComplete(formatted)) {
      const err = validateDobDateFromDisplayString(t, formatted, true, SENDER_DOB_AGE_OPTIONS);
      setErrors((prev) => ({ ...prev, dob: err }));
      if (!err) {
        const parsed = dateFromDobDisplayString(formatted);
        if (parsed) {
          try { setSenderDOB?.(parsed); } catch {}
          await verifyDOBWithID(formatted);
        }
      }
    } else if (!formatted.trim()) {
      setErrors((prev) => ({ ...prev, dob: t("SENDER_DOB_ERROR_REQUIRED") }));
    } else {
      setErrors((prev) => ({ ...prev, dob: "" }));
    }
  };

  return (
    <View style={styles.container} testID={testID}>
      <Text style={styles.title}>{t("SENDER_DOB_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("SENDER_DOB_SUBTITLE")}</Text>

      <FloatingTextInputComponent
        testID="senderDOB"
        isAutoFocus={true}
        value={dob}
        onChangeText={handleManualInput}
        errorText={errors.dob}
        onClearError={() => setErrors((prev) => ({ ...prev, dob: "" }))}
        keyboardType={"number-pad"}
        dobIsComplete={isDobDisplayComplete(dob)}
      />
      {/* Bottom Popup Confirmation */}
      <View style={styles.infoBox}>
        <Text style={styles.infoText}>
          {t("YOUR_INFO_IS_ALWAYS_KEPT_PRIVATE")}
        </Text>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  input: { flex: 1, fontSize: 16 },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  modalHeaderRow: {
    alignSelf: 'flex-end',
    marginBottom: 48
  },
  modalTitle: {
    fontFamily: 'Rubik',
    fontSize: 24,
    fontWeight: "500",
    color: Colors.accountLock,
    lineHeight: 28,
    marginVertical: 26
  },
  modalInfo: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: "400",
    color: Colors.accountLock,
    lineHeight: 18,
    marginBottom: 26,
    textAlign: 'center',
  },
  bottomSheet: {
    backgroundColor: Colors.white,
    padding: 24,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  modalDOB: {
    fontFamily: 'Rubik',
    fontSize: 32,
    fontWeight: "500",
    color: Colors.accountLock,
    // marginTop: 10,
    marginBottom: 48,
  },
  confirmBtn: {
    backgroundColor: "#000",
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
    height: 48
  },
  cancelBtn: { paddingTop: 24, alignItems: "center" },
  cancelText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "500",
    color: Colors.darkGrey,
    lineHeight: 24,
  },
  infoBox: {
    marginTop: 20,
    padding: 12,
    borderWidth: 1,
    borderColor: "#FFF",
    borderRadius: 8,
  },
  infoText: { fontSize: 14, color: "#FFF" },
  footer: {
    // paddingHorizontal: 24,
    paddingBottom: Platform.OS === "ios" ? 24 : 16,
    backgroundColor: Colors.white
  },
});

export default SenderDateOfBirthStep;